import { SafeText } from "@/shared/components/SafeText";
import { sanitizeDisplayText } from "@/shared/lib/safe-text";
import { idsWithSharedName, shortId } from "./location-label";
import type { OrganizationLocation } from "./use-organization-locations";

interface LocationChecklistProps {
  readonly locations: readonly OrganizationLocation[];
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (id: string, name: string) => void;
  /** Ids that cannot be toggled (already assigned, saving...). */
  readonly disabledIds?: readonly string[];
  /** Checkbox test ids are `${testIdPrefix}-${locationId}`. */
  readonly testIdPrefix: string;
  readonly label: string;
}

/** Checkbox list of the locations of ONE organization. Names are user-chosen: rendered via SafeText. */
export function LocationChecklist({
  locations,
  selected,
  onToggle,
  disabledIds = [],
  testIdPrefix,
  label,
}: LocationChecklistProps) {
  // A Set: the list can hold 100 rows and the disabled ids as many.
  const disabled = new Set(disabledIds);
  const needsSuffix = idsWithSharedName(locations);
  return (
    <ul
      aria-label={label}
      className="max-h-60 divide-y divide-border overflow-y-auto rounded-md border border-border"
    >
      {locations.map((location) => {
        const readableName = sanitizeDisplayText(location.name);
        const suffix = needsSuffix.has(location.id) ? shortId(location.id) : null;
        return (
          <li key={location.id} className="flex min-w-0 items-center gap-3 px-3 py-2 text-sm">
            <input
              type="checkbox"
              data-testid={`${testIdPrefix}-${location.id}`}
              aria-label={suffix ? `${readableName} ${suffix}` : readableName || location.id}
              checked={selected.has(location.id)}
              disabled={disabled.has(location.id)}
              onChange={() => onToggle(location.id, location.name)}
              className="h-4 w-4 shrink-0 accent-primary"
            />
            <span
              className={
                readableName
                  ? "min-w-0 flex-1 truncate"
                  : "min-w-0 flex-1 truncate font-mono text-xs text-muted-foreground"
              }
            >
              <SafeText fallback={location.id}>{location.name}</SafeText>
            </span>
            {suffix && (
              <span
                data-testid={`${testIdPrefix}-${location.id}-suffix`}
                className="shrink-0 font-mono text-xs text-muted-foreground"
              >
                {suffix}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}
